import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { CHAT } from "@/constants/testIds";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Aegis, your Treasurescare concierge. Ask me about plans, claims, or repairs." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || sending) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: txt }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", { session_id: sessionId, message: txt });
      setSessionId(data.session_id);
      setMessages(m => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "I couldn't reach my assistant. Try again shortly." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid={CHAT.openBtn}
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-24 lg:bottom-6 right-6 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-teal-glow flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-secondary" />
        </button>
      )}
      {open && (
        <div
          data-testid={CHAT.panel}
          className="fixed z-40 bottom-24 lg:bottom-6 right-6 w-[92vw] sm:w-[380px] h-[520px] bg-card border border-border rounded-2xl shadow-teal-glow flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="px-5 py-4 bg-primary text-primary-foreground flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-secondary" />
              <div>
                <div className="font-display font-semibold">Aegis</div>
                <div className="text-[10px] opacity-70 overline">AI CONCIERGE · GEMINI</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="opacity-70 hover:opacity-100"><X className="w-5 h-5" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
            {messages.map((m, i) => (
              <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-primary text-primary-foreground rounded-br-sm"
                    : "bg-muted text-foreground rounded-bl-sm"
                }`}>{m.content}</div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-muted text-muted-foreground rounded-2xl rounded-bl-sm px-4 py-2 text-sm">Thinking…</div>
              </div>
            )}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-border flex gap-2 bg-card">
            <Input
              data-testid={CHAT.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask about plans, claims, repairs..."
              className="flex-1"
              disabled={sending}
            />
            <Button data-testid={CHAT.send} type="submit" disabled={sending || !input.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
